/**
 * LAYERS — Properties panel.
 *
 * The right-hand inspector, rebuilt to the same information architecture as the
 * Shaders design editor, with everything this app needs on top:
 *
 *   Transform  ·  Sizing  ·  Appearance  ·  <component props>  ·  Layer
 *
 * Every prop row carries a "make value dynamic" affordance so a static value
 * can be bound to a driver (cursor, time, or another layer) without leaving
 * the panel — the most valuable control in the reference product, and the one
 * this app was missing entirely.
 *
 * Rendering is declarative: `buildPropsPanel(host, selected, def, handlers)`
 * writes the whole panel and wires every control in one pass. State lives in
 * `selected.params`; nothing here keeps a second copy that can drift.
 */

import {
  slider,
  numberField,
  colorField,
  segmented,
  select,
  toggle,
  section,
  sectionNote,
  type Control,
} from "./controls";
import { iconSvg } from "./icons";
import type { StackItem } from "@layers/stack";
import type { CoreDef } from "./main";

/* ── Blend modes ──────────────────────────────────────────────────────────
   Mirrors the reference editor's list, including the two colour-space-qualified
   "Normal" entries, because they are genuinely different blend results. */
export const BLEND_MODES = [
  "normal",
  "normal-oklch",
  "normal-oklab",
  "multiply",
  "screen",
  "linear-dodge",
  "linear-burn",
  "color-dodge",
  "color-burn",
  "overlay",
  "soft-light",
  "hard-light",
  "difference",
  "exclusion",
  "darken",
  "lighten",
  "hue",
  "saturation",
  "color",
  "luminosity",
] as const;

export function blendLabel(v: string): string {
  if (v === "normal-oklch") return "Normal (OKLCh)";
  if (v === "normal-oklab") return "Normal (OKLAB)";
  return v
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const MASK_CHANNELS = ["alpha", "luminance", "red", "green", "blue"];
const OBJECT_FITS = ["fill", "cover", "contain", "scale-down"];

export const COLOR_SPACES = ["srgb", "linear-rgb", "oklab", "oklch", "hsl", "hsv", "lab", "lch", "p3"];
export const TONE_MAPPINGS = ["linear", "aces-filmic", "neutral", "agx", "reinhard"];

/* ── Dynamic-prop driver ────────────────────────────────────────────────── */

export type DynamicMode = "off" | "cursor" | "auto" | "map";

export interface DynamicState {
  mode: DynamicMode;
  axis: "x" | "y";
  min: number;
  max: number;
  curve: number;
  smoothing: number;
  momentum: number;
  speed: number;
  easing: string;
  reach: number;
  source: string;
  channel: string;
  lockX: boolean;
  lockY: boolean;
  invertX: boolean;
  invertY: boolean;
}

export function emptyDynamic(): DynamicState {
  return {
    mode: "off",
    axis: "x",
    min: 0,
    max: 100,
    curve: 0,
    smoothing: 0.1,
    momentum: 0,
    speed: 1,
    easing: "sine",
    reach: 1,
    source: "",
    channel: "alpha",
    lockX: false,
    lockY: false,
    invertX: false,
    invertY: false,
  };
}

/** A stored value is "dynamic" when it carries our `dynamic: true` marker. */
export function readDynamic(value: unknown): DynamicState | null {
  if (value && typeof value === "object" && (value as Record<string, unknown>).dynamic === true) {
    return { ...emptyDynamic(), ...(value as Record<string, unknown>) } as DynamicState;
  }
  return null;
}

/**
 * Expands a dynamic state into the object the reference editor's Export dialog
 * writes, so a dynamic prop survives a preset round-trip and is legible when
 * the preset is opened as JSON.
 */
export function expandDynamic(state: DynamicState): Record<string, unknown> {
  if (state.mode === "auto") {
    return {
      dynamic: true,
      type: "auto-animate",
      mode: "ping-pong",
      outputMin: state.min,
      outputMax: state.max,
      speed: state.speed,
      easing: state.easing,
    };
  }
  if (state.mode === "cursor") {
    return {
      dynamic: true,
      type: "mouse-position",
      axis: state.axis,
      outputMin: state.min,
      outputMax: state.max,
      curve: state.curve,
      smoothing: state.smoothing,
      momentum: state.momentum,
      reach: state.reach,
      lockX: state.lockX,
      lockY: state.lockY,
      invertX: state.invertX,
      invertY: state.invertY,
    };
  }
  return {
    dynamic: true,
    type: "map",
    source: state.source,
    channel: state.channel,
    outputMin: state.min,
    outputMax: state.max,
    smoothing: state.smoothing,
  };
}

/** Merges a new dynamic state into an existing stored value object. */
function withDynamic(prev: unknown, next: DynamicState): Record<string, unknown> {
  const base = prev && typeof prev === "object" ? (prev as Record<string, unknown>) : {};
  return { ...base, ...expandDynamic(next) };
}

/** The plain value a param reverts to when its driver is switched off. */
function staticDefault(param: CoreDef["params"][number]): unknown {
  return param.default;
}

/* ── Panel ──────────────────────────────────────────────────────────────── */

export interface PropHandlers {
  setParam: (key: string, value: unknown) => void;
  setItem: (patch: Record<string, unknown>) => void;
  editMask: () => void;
  allItems: () => StackItem[];
}

export function buildPropsPanel(
  host: HTMLElement,
  selected: StackItem,
  def: CoreDef,
  handlers: PropHandlers,
): void {
  host.innerHTML = "";
  const all = handlers.allItems();
  const p = selected.params;

  /* Header */
  const head = document.createElement("div");
  head.className = "props-head";
  head.innerHTML = `
    <div class="props-head-title">
      <span class="props-head-icon">${iconSvg(def.iconName ?? "cores")}</span>
      <span class="props-head-name">${def.label}</span>
    </div>
    <button type="button" class="icon-btn" data-props-docs title="Component documentation" aria-label="Component documentation">
      ${iconSvg("doc")}
    </button>`;
  host.appendChild(head);

  /* ── Transform ─────────────────────────────────────────────────────── */
  const s = section({ title: "Transform", icon: "move", open: true });
  s.body.appendChild(
    twoUp(
      unitSlider("X", Number(p.offsetX ?? 0), -100, 100, 1, "%"),
      unitSlider("Y", Number(p.offsetY ?? 0), -100, 100, 1, "%"),
      (c, v) => handlers.setParam("offsetX", v),
      (c, v) => handlers.setParam("offsetY", v),
    ),
  );
  s.body.appendChild(
    twoUp(
      unitSlider("Rotation", Number(p.rotation ?? 0), 0, 360, 1, "°"),
      slider({ label: "Opacity", min: 0, max: 1, step: 0.01, value: selected.opacity }),
      (c, v) => handlers.setParam("rotation", v),
      (c, v) => handlers.setItem({ opacity: Number(v) }),
    ),
  );
  host.appendChild(s.el);

  /* ── Sizing ────────────────────────────────────────────────────────── */
  const sz = section({ title: "Sizing", icon: "frame", open: true });
  const fit = segmented({
    label: "Object Fit",
    value: String(p.objectFit ?? "cover"),
    options: OBJECT_FITS.map((f) => ({ value: f, label: f, title: `Object fit: ${f}` })),
  });
  fit.onChange((v) => handlers.setParam("objectFit", v));
  sz.body.appendChild(fit.el);

  sz.body.appendChild(
    twoUp(
      unitSlider("Width", Number(p.width ?? 100), 0, 400, 1, "%"),
      unitSlider("Height", Number(p.height ?? 100), 0, 400, 1, "%"),
      (c, v) => handlers.setParam("width", v),
      (c, v) => handlers.setParam("height", v),
    ),
  );
  host.appendChild(sz.el);

  /* ── Appearance ────────────────────────────────────────────────────── */
  const ap = section({ title: "Appearance", icon: "blend", open: true });

  const opacity = slider({
    label: "Opacity",
    min: 0,
    max: 1,
    step: 0.01,
    value: selected.opacity,
    format: (v) => `${Math.round(v * 100)}`,
  });
  opacity.onChange((v) => handlers.setItem({ opacity: Number(v) }));
  ap.body.appendChild(opacity.el);

  const blend = select({
    label: "Blend Mode",
    value: selected.blend,
    options: BLEND_MODES.map((m) => ({ value: m, label: blendLabel(m) })),
    wide: true,
  });
  blend.onChange((v) => handlers.setItem({ blend: String(v) }));
  ap.body.appendChild(blend.el);

  const maskSource = select({
    label: "Mask Source",
    value: String((selected.params.maskSource as unknown as string) ?? "none"),
    options: [
      { value: "none", label: "None" },
      ...all.filter((i) => i.id !== selected.id).map((i) => ({ value: i.id, label: layerName(i, all) })),
    ],
    wide: true,
  });
  maskSource.onChange((v) => handlers.setParam("maskSource", v === "none" ? null : String(v)));
  ap.body.appendChild(maskSource.el);

  const maskChannel = select({
    label: "Mask Channel",
    value: String((selected.params.maskChannel as unknown as string) ?? "alpha"),
    options: MASK_CHANNELS.map((c) => ({ value: c, label: c[0].toUpperCase() + c.slice(1) })),
  });
  maskChannel.onChange((v) => handlers.setParam("maskChannel", String(v)));
  ap.body.appendChild(maskChannel.el);

  const maskBtn = document.createElement("button");
  maskBtn.type = "button";
  maskBtn.className = "btn btn-block";
  maskBtn.innerHTML = `${iconSvg("mask")}<span>Edit Mask</span>`;
  maskBtn.addEventListener("click", () => handlers.editMask());
  ap.body.appendChild(maskBtn);

  host.appendChild(ap.el);

  /* ── Component props ───────────────────────────────────────────────── */
  if (def.params.length) {
    const own = section({ title: def.label, icon: "cores", open: true });
    own.body.appendChild(sectionNote(def.blurb ?? `${def.params.length} parameters`));
    for (const param of def.params) own.body.appendChild(paramControl(param, selected, handlers));
    host.appendChild(own.el);
  }

  /* ── Layer actions ─────────────────────────────────────────────────── */
  const act = section({ title: "Layer", icon: "layers", open: false });

  const row = document.createElement("div");
  row.className = "btn-row";
  const up = document.createElement("button");
  up.type = "button";
  up.className = "btn";
  up.innerHTML = `${iconSvg("plus")}<span>Move Up</span>`;
  up.addEventListener("click", () => handlers.setItem({ __move: "up" }));
  const down = document.createElement("button");
  down.type = "button";
  down.className = "btn";
  down.innerHTML = `${iconSvg("minus")}<span>Move Down</span>`;
  down.addEventListener("click", () => handlers.setItem({ __move: "down" }));
  row.append(up, down);
  act.body.appendChild(row);

  const vis = toggle({ label: "Visible", value: selected.enabled });
  vis.onChange((v) => handlers.setItem({ enabled: Boolean(v) }));
  act.body.appendChild(vis.el);

  const rm = document.createElement("button");
  rm.type = "button";
  rm.className = "btn btn-block btn-danger";
  rm.innerHTML = `${iconSvg("close")}<span>Remove Layer</span>`;
  rm.addEventListener("click", () => handlers.setItem({ __remove: true }));
  act.body.appendChild(rm);

  host.appendChild(act.el);
}

/** Human name for a layer — its core, disambiguated by position. */
export function layerName(item: StackItem, all: StackItem[]): string {
  const idx = all.findIndex((i) => i.id === item.id);
  return `${item.core} ${idx + 1}`;
}

/**
 * Two controls sharing one row. `bindA`/`bindB` receive the control and its
 * value so the caller decides whether it is a param or a layer-level change.
 */
function twoUp(
  a: Control,
  b: Control,
  bindA: (c: Control, v: unknown) => void,
  bindB: (c: Control, v: unknown) => void,
): HTMLElement {
  a.onChange((v) => bindA(a, v));
  b.onChange((v) => bindB(b, v));
  const g = document.createElement("div");
  g.className = "ctl-grid";
  g.append(a.el, b.el);
  return g;
}

/** Slider that can flip between % and px, matching the reference editor. */
function unitSlider(label: string, value: number, min: number, max: number, step: number, unit: string): Control {
  return slider({
    label,
    min,
    max,
    step,
    value,
    unit: unit === "°" ? undefined : (unit as "%" | "px"),
    steppers: true,
    format: (v) => String(Math.round(v * 10) / 10),
  });
}

/* ── Per-param control + dynamic-prop affordance ────────────────────────── */

function paramControl(
  param: CoreDef["params"][number],
  selected: StackItem,
  handlers: PropHandlers,
): HTMLElement {
  const raw = selected.params[param.key] ?? param.default;
  let dyn = readDynamic(raw);

  const wrap = document.createElement("div");
  wrap.className = "param";

  const body = document.createElement("div");
  body.className = "param-body";
  wrap.appendChild(body);

  let control: Control;

  if (param.type === "slider") {
    const shown = dyn ? dyn.min : Number(raw);
    control = slider({
      label: param.label,
      min: param.min ?? 0,
      max: param.max ?? 100,
      step: param.step ?? 1,
      value: shown,
      steppers: true,
    });
    control.onChange((v) => {
      const n = Number(v);
      handlers.setParam(param.key, dyn ? withDynamic(raw, { ...dyn, min: n }) : n);
    });
  } else if (param.type === "color") {
    control = colorField({ label: param.label, value: typeof raw === "string" ? raw : "#000000", showHex: true });
    control.onChange((v) => handlers.setParam(param.key, String(v)));
  } else if (param.type === "select") {
    control = select({
      label: param.label,
      value: String(raw),
      options: param.options ?? [],
      wide: true,
    });
    control.onChange((v) => handlers.setParam(param.key, String(v)));
  } else if (param.type === "toggle") {
    control = toggle({ label: param.label, value: Boolean(raw) });
    control.onChange((v) => handlers.setParam(param.key, Boolean(v)));
  } else {
    control = numberField({ label: param.label, value: Number(raw), step: param.step ?? 1 });
    control.onChange((v) => handlers.setParam(param.key, Number(v)));
  }

  body.appendChild(control.el);

  /* The affordance: turns any static prop into a drivable one. */
  const dynBtn = document.createElement("button");
  dynBtn.type = "button";
  dynBtn.className = `icon-btn dyn-btn${dyn ? " is-active" : ""}`;
  dynBtn.title = dyn ? `Value is dynamic (${dyn.mode})` : "Make value dynamic";
  dynBtn.setAttribute("aria-label", dyn ? "Configure dynamic value" : `Make ${param.label} dynamic`);
  dynBtn.setAttribute("aria-pressed", String(Boolean(dyn)));
  dynBtn.innerHTML = iconSvg("dynamic");
  body.appendChild(dynBtn);

  let dynHost: HTMLElement | null = null;
  const open = () => {
    if (!dynHost) {
      dynHost = document.createElement("div");
      dynHost.className = "dyn-editor";
      wrap.appendChild(dynHost);
    }
    renderDynamicEditor(dynHost, param, selected, handlers, () => dyn);
    dyn = readDynamic(selected.params[param.key]);
  };
  dynBtn.addEventListener("click", () => {
    wrap.classList.toggle("is-dyn-open");
    if (wrap.classList.contains("is-dyn-open")) open();
  });

  return wrap;
}

function renderDynamicEditor(
  host: HTMLElement,
  param: CoreDef["params"][number],
  selected: StackItem,
  handlers: PropHandlers,
  getDyn: () => DynamicState | null,
): void {
  host.innerHTML = "";
  const state = getDyn() ?? emptyDynamic();
  const raw = selected.params[param.key] ?? param.default;
  const commit = (next: DynamicState) =>
    handlers.setParam(param.key, next.mode === "off" ? staticDefault(param) : withDynamic(raw, next));

  const mode = segmented({
    label: "Driver",
    value: state.mode,
    options: [
      { value: "off", label: "Off" },
      { value: "cursor", label: "Cursor" },
      { value: "auto", label: "Auto" },
      { value: "map", label: "Map" },
    ],
  });
  mode.onChange((v) => commit({ ...state, mode: v as DynamicMode }));
  host.appendChild(mode.el);

  if (state.mode === "off") {
    const note = document.createElement("p");
    note.className = "prop-note";
    note.textContent = "Static value. Pick a driver to bind this prop to time, the cursor, or another layer.";
    host.appendChild(note);
    return;
  }

  if (state.mode === "cursor") {
    const axis = segmented({
      label: "Axis",
      value: state.axis,
      options: [
        { value: "x", label: "X" },
        { value: "y", label: "Y" },
      ],
    });
    axis.onChange((v) => commit({ ...state, axis: v as "x" | "y" }));
    host.appendChild(axis.el);
  }

  if (state.mode === "map") {
    const all = handlers.allItems();
    const src = select({
      label: "Source",
      value: state.source || "none",
      options: [
        { value: "none", label: "None" },
        ...all.filter((i) => i.id !== selected.id).map((i) => ({ value: i.id, label: layerName(i, all) })),
      ],
      wide: true,
    });
    src.onChange((v) => commit({ ...state, source: String(v) }));
    host.appendChild(src.el);

    const ch = select({
      label: "Channel",
      value: state.channel,
      options: MASK_CHANNELS.map((c) => ({ value: c, label: c[0].toUpperCase() + c.slice(1) })),
    });
    ch.onChange((v) => commit({ ...state, channel: String(v) }));
    host.appendChild(ch.el);
  }

  if (state.mode === "auto") {
    const speed = slider({ label: "Speed", min: 0, max: 4, step: 0.1, value: state.speed, format: (v) => String(Math.round(v * 10) / 10) });
    speed.onChange((v) => commit({ ...state, speed: Number(v) }));
    host.appendChild(speed.el);

    const ease = select({
      label: "Easing",
      value: state.easing,
      options: ["sine", "linear", "quad", "expo", "bounce"].map((e) => ({ value: e, label: e[0].toUpperCase() + e.slice(1) })),
    });
    ease.onChange((v) => commit({ ...state, easing: String(v) }));
    host.appendChild(ease.el);
  }

  if (state.mode !== "auto") {
    host.appendChild(
      twoUp(
        slider({ label: "Min", min: -1000, max: 1000, step: 1, value: state.min, format: (v) => String(Math.round(v)) }),
        slider({ label: "Max", min: -1000, max: 1000, step: 1, value: state.max, format: (v) => String(Math.round(v)) }),
        (_c, v) => commit({ ...state, min: Number(v) }),
        (_c, v) => commit({ ...state, max: Number(v) }),
      ),
    );
  }

  host.appendChild(
    twoUp(
      slider({ label: "Smoothing", min: 0, max: 1, step: 0.05, value: state.smoothing, format: (v) => String(Math.round(v * 100) / 100) }),
      slider({ label: "Momentum", min: 0, max: 1, step: 0.05, value: state.momentum, format: (v) => String(Math.round(v * 100) / 100) }),
      (_c, v) => commit({ ...state, smoothing: Number(v) }),
      (_c, v) => commit({ ...state, momentum: Number(v) }),
    ),
  );

  if (state.mode === "cursor") {
    const curve = slider({ label: "Curve", min: -1, max: 1, step: 0.05, value: state.curve, format: (v) => String(Math.round(v * 100) / 100) });
    curve.onChange((v) => commit({ ...state, curve: Number(v) }));
    host.appendChild(curve.el);

    const reach = slider({ label: "Reach", min: 0, max: 2, step: 0.05, value: state.reach, format: (v) => String(Math.round(v * 100) / 100) });
    reach.onChange((v) => commit({ ...state, reach: Number(v) }));
    host.appendChild(reach.el);

    const adj = section({ title: "Adjustments", icon: "adjust", open: false });
    const mk = (label: string, key: "lockX" | "lockY" | "invertX" | "invertY") => {
      const t = toggle({ label, value: state[key] });
      t.onChange((v) => commit({ ...state, [key]: Boolean(v) }));
      return t.el;
    };
    adj.body.append(
      mk("Lock X", "lockX"),
      mk("Lock Y", "lockY"),
      mk("Invert Horizontal", "invertX"),
      mk("Invert Vertical", "invertY"),
    );
    host.appendChild(adj.el);
  }
}

/** Formats a stored param value for display without mutating it. */
export function describeValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (o.dynamic === true) return String(o.type ?? o.mode ?? "dynamic");
    return JSON.stringify(o);
  }
  if (typeof v === "number") return String(Math.round(v * 1000) / 1000);
  return String(v);
}

export function colorSpaceOptions(): { value: string; label: string }[] {
  return COLOR_SPACES.map((c) => ({ value: c, label: c.toUpperCase().replace("-", " ") }));
}

export function toneMappingOptions(): { value: string; label: string }[] {
  return TONE_MAPPINGS.map((t) => ({ value: t, label: t.replace("-", " ").replace(/\b\w/g, (m) => m.toUpperCase()) }));
}
